import { useState, useEffect, useCallback, useRef } from 'react'
import { useUser } from '../../contexts/UserContext'
import { vendorsAPI, vendorDocumentsAPI, Vendor, VendorProduct } from '../../lib/services'
import { trackEvent } from '../../services/tracking'
import BasicProfileCard from '../../components/vendor/BasicProfileCard'
import ComplianceProfileCard from '../../components/vendor/ComplianceProfileCard'
import OpportunitiesInbox from '../../components/vendor/OpportunitiesInbox'
import ProductCatalog from '../../components/vendor/ProductCatalog'
import ContactsCard from '../../components/vendor/ContactsCard'
import VendorProductOverlay from './VendorProductOverlay'
import FileDropzone from '../../components/ui/FileDropzone'
import VendorDocumentList from '../../components/vendor/VendorDocumentList'

function EditableDescription({ value, vendorId, onSaved }: {
  value: string
  vendorId: number
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [saving, setSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { setEditValue(value) }, [value])

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'
    }
  }, [editing])

  const save = async () => {
    if (editValue === value) { setEditing(false); return }
    setSaving(true)
    try {
      await vendorsAPI.update(vendorId, { description: editValue } as any)
      onSaved()
    } catch (err) {
      console.error('Failed to save description:', err)
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  if (editing) {
    return (
      <textarea
        ref={textareaRef}
        value={editValue}
        onChange={e => {
          setEditValue(e.target.value)
          e.target.style.height = 'auto'
          e.target.style.height = e.target.scrollHeight + 'px'
        }}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Escape') setEditing(false) }}
        disabled={saving}
        className="w-full mt-3 px-0 py-1 text-sm text-foreground bg-transparent border-b border-primary/40 resize-none focus:outline-none"
        placeholder="Add a company description..."
        rows={1}
      />
    )
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="block w-full text-left mt-3"
    >
      {value ? (
        <p className="text-sm text-muted-foreground hover:text-foreground transition-colors">{value}</p>
      ) : (
        <p className="text-sm text-muted-foreground/50 italic hover:text-primary transition-colors">Add a company description...</p>
      )}
    </button>
  )
}

export default function VendorProfile() {
  const { user } = useUser()
  const [vendor, setVendor] = useState<Vendor | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<VendorProduct | null>(null)
  const [docRefresh, setDocRefresh] = useState(0)

  const fetchVendor = useCallback(async () => {
    if (!user?.vendor_id) return
    try {
      const data = await vendorsAPI.get(user.vendor_id)
      setVendor(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load vendor profile')
    } finally {
      setLoading(false)
    }
  }, [user?.vendor_id])

  useEffect(() => {
    fetchVendor()
  }, [fetchVendor])

  if (!user?.vendor_id) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-muted-foreground">
          <p className="text-lg">No vendor profile linked</p>
          <p className="text-sm mt-1">Contact your administrator to link your account to a vendor.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading profile...</div>
      </div>
    )
  }

  if (error || !vendor) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">{error || 'Vendor not found'}</div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
      <div className="mb-10">
        <h1 className="text-2xl font-bold text-foreground">{vendor.name}</h1>
        {vendor.headquarters_location && (
          <p className="text-sm text-muted-foreground mt-1">{vendor.headquarters_location}</p>
        )}
        <EditableDescription
          value={vendor.description || ''}
          vendorId={vendor.id}
          onSaved={fetchVendor}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <BasicProfileCard
          vendor={vendor}
          onRefresh={fetchVendor}
        />
        <ComplianceProfileCard
          vendor={vendor}
          onRefresh={fetchVendor}
        />
        <ContactsCard
          vendorId={vendor.id}
          teamMembers={vendor.team_members || []}
          onRefresh={fetchVendor}
        />
      </div>

      <div className="mb-10">
        <OpportunitiesInbox vendorId={vendor.id} />
      </div>

      <div className="mb-10">
        <ProductCatalog
          products={vendor.products || []}
          vendorId={vendor.id}
          onProductClick={(product) => {
            trackEvent('vendor_product_viewed', { vendor_id: vendor.id, product_id: product.id, product_name: product.name })
            setSelectedProduct(product)
          }}
          onRefresh={fetchVendor}
        />
      </div>

      <div className="mb-10">
        <div className="border border-border rounded-lg p-6">
          <h3 className="text-base font-semibold text-foreground mb-4">Documents</h3>
          <FileDropzone
            onFilesSelected={async (files) => {
              for (const file of files) {
                try {
                  await vendorDocumentsAPI.upload(file, vendor.id, 'other')
                } catch (err) {
                  console.error('Failed to upload document:', err)
                }
              }
              setDocRefresh(n => n + 1)
            }}
            label="Drop datasheets, spec sheets, or company documents here"
          />
          <VendorDocumentList vendorId={vendor.id} onSpecsExtracted={() => { fetchVendor(); setDocRefresh(n => n + 1) }} refreshTrigger={docRefresh} />
        </div>
      </div>

      {selectedProduct && (
        <VendorProductOverlay
          product={selectedProduct}
          vendorId={vendor.id}
          onClose={() => setSelectedProduct(null)}
          onRefresh={fetchVendor}
        />
      )}
    </div>
  )
}
